import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';

// ─── Helpers de data ──────────────────────────────────────────────────────────
const MESES = ['Janeiro','Fevereiro','Março','Abril','Maio','Junho','Julho','Agosto','Setembro','Outubro','Novembro','Dezembro'];
const DIAS_SEMANA = ['Dom','Seg','Ter','Qua','Qui','Sex','Sáb'];

function classeVencimento(nextDate) {
  if (!nextDate) return null;
  const dias = Math.ceil((new Date(nextDate) - new Date()) / (1000 * 60 * 60 * 24));
  if (dias < 0) return { cor: 'bg-red-900/70 text-red-200 border-red-700', dot: 'bg-red-400', label: 'Vencido' };
  if (dias <= 30) return { cor: 'bg-amber-900/70 text-amber-200 border-amber-700', dot: 'bg-amber-400', label: `${dias}d` };
  return { cor: 'bg-emerald-900/50 text-emerald-200 border-emerald-800', dot: 'bg-emerald-400', label: `${dias}d` };
}

function diasNoMes(ano, mes) {
  return new Date(ano, mes + 1, 0).getDate();
}

function primeiroDiaSemana(ano, mes) {
  return new Date(ano, mes, 1).getDay();
}

// ─── Componente principal ─────────────────────────────────────────────────────
export default function CalendarPage() {
  const [instrumentos, setInstrumentos] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [visao, setVisao] = useState('mensal'); // 'mensal' | 'trimestral'
  const [mes, setMes] = useState(new Date().getMonth());
  const [ano, setAno] = useState(new Date().getFullYear());
  const [diaExpandido, setDiaExpandido] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    async function buscar() {
      try {
        const { data } = await api.get('/instruments?calibratable=true&limit=500');
        setInstrumentos(data.dados || data);
      } catch (err) {
        console.error(err);
      } finally {
        setCarregando(false);
      }
    }
    buscar();
  }, []);

  // ─── Indexa instrumentos por data de vencimento ───────────────────────────
  const porDia = useMemo(() => {
    const mapa = {};
    instrumentos.forEach((inst) => {
      if (!inst.nextCalibrationDate) return;
      const d = new Date(inst.nextCalibrationDate);
      const chave = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
      if (!mapa[chave]) mapa[chave] = [];
      mapa[chave].push(inst);
    });
    return mapa;
  }, [instrumentos]);

  // ─── Instrumentos dos próximos 3 meses (visão trimestral) ────────────────
  const trimestral = useMemo(() => {
    const resultado = [];
    for (let i = 0; i < 3; i++) {
      const m = (mes + i) % 12;
      const a = ano + Math.floor((mes + i) / 12);
      const insts = instrumentos.filter((inst) => {
        if (!inst.nextCalibrationDate) return false;
        const d = new Date(inst.nextCalibrationDate);
        return d.getMonth() === m && d.getFullYear() === a;
      }).sort((a, b) => new Date(a.nextCalibrationDate) - new Date(b.nextCalibrationDate));
      resultado.push({ mes: m, ano: a, instrumentos: insts });
    }
    return resultado;
  }, [instrumentos, mes, ano]);

  // ─── Navegação entre meses ────────────────────────────────────────────────
  function mesAnterior() {
    if (mes === 0) { setMes(11); setAno(a => a - 1); }
    else setMes(m => m - 1);
    setDiaExpandido(null);
  }
  function proximoMes() {
    if (mes === 11) { setMes(0); setAno(a => a + 1); }
    else setMes(m => m + 1);
    setDiaExpandido(null);
  }
  function voltarHoje() {
    setMes(new Date().getMonth());
    setAno(new Date().getFullYear());
    setDiaExpandido(null);
  }

  function irParaInstrumento(inst) {
    navigate(`/instruments?status=${inst.calibrationStatus}`);
  }

  // ─── Grid do mês ─────────────────────────────────────────────────────────
  const totalDias = diasNoMes(ano, mes);
  const offsetInicio = primeiroDiaSemana(ano, mes);
  const hoje = new Date();
  const ehHoje = (dia) =>
    dia === hoje.getDate() && mes === hoje.getMonth() && ano === hoje.getFullYear();

  if (carregando) {
    return (
      <div className="p-8">
        <div className="h-8 w-48 bg-[var(--surface-4)] rounded animate-pulse mb-6" />
        <div className="grid grid-cols-7 gap-2">
          {Array.from({ length: 35 }).map((_, i) => (
            <div key={i} className="h-24 bg-[var(--surface-3)] rounded-lg animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 min-h-full bg-[var(--surface-1)]">

      {/* ── Cabeçalho ──────────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-xl font-semibold text-slate-100">Calendário de Vencimentos</h1>
          <p className="text-slate-500 text-sm mt-0.5">
            {instrumentos.length} instrumentos calibráveis monitorados
          </p>
        </div>

        {/* Controles de visão */}
        <div className="flex items-center gap-3">
          <div className="flex bg-[var(--surface-3)] border border-[var(--surface-4)] rounded-lg overflow-hidden text-sm">
            {['mensal', 'trimestral'].map((v) => (
              <button
                key={v}
                onClick={() => setVisao(v)}
                className={`px-3 py-2 capitalize transition-colors ${
                  visao === v ? 'bg-blue-700 text-white' : 'text-slate-400 hover:text-slate-200'
                }`}
              >
                {v === 'mensal' ? '📅 Mensal' : '📆 Trimestral'}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ── Legenda ──────────────────────────────────────────────────────────── */}
      <div className="flex items-center gap-4 mb-5 text-xs text-slate-500">
        <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-emerald-400" /> Em dia (&gt;30 dias)</span>
        <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-amber-400" /> A vencer (≤30 dias)</span>
        <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-red-400" /> Vencido</span>
      </div>

      {/* ── Visão Mensal ─────────────────────────────────────────────────────── */}
      {visao === 'mensal' && (
        <>
          {/* Navegação do mês */}
          <div className="flex items-center justify-between mb-4">
            <button onClick={mesAnterior} className="p-2 text-slate-400 hover:text-slate-200 hover:bg-[var(--surface-3)] rounded-lg transition-colors">
              ← 
            </button>
            <div className="flex items-center gap-3">
              <h2 className="text-lg font-semibold text-slate-200">
                {MESES[mes]} {ano}
              </h2>
              <button
                onClick={voltarHoje}
                className="text-xs text-blue-400 hover:text-blue-300 border border-blue-800/50 px-2 py-0.5 rounded"
              >
                Hoje
              </button>
            </div>
            <button onClick={proximoMes} className="p-2 text-slate-400 hover:text-slate-200 hover:bg-[var(--surface-3)] rounded-lg transition-colors">
              →
            </button>
          </div>

          {/* Grid de dias da semana */}
          <div className="grid grid-cols-7 gap-1 mb-1">
            {DIAS_SEMANA.map((d) => (
              <div key={d} className="text-center text-xs text-slate-600 py-1 font-medium">{d}</div>
            ))}
          </div>

          {/* Grid de dias */}
          <div className="grid grid-cols-7 gap-1">
            {/* Offset do primeiro dia */}
            {Array.from({ length: offsetInicio }).map((_, i) => (
              <div key={`offset-${i}`} className="h-24 rounded-lg" />
            ))}

            {/* Dias do mês */}
            {Array.from({ length: totalDias }).map((_, i) => {
              const dia = i + 1;
              const chave = `${ano}-${mes}-${dia}`;
              const instsNoDia = porDia[chave] || [];
              const isHoje = ehHoje(dia);
              const expandido = diaExpandido === chave;

              return (
                <div
                  key={dia}
                  onClick={() => instsNoDia.length > 0 && setDiaExpandido(expandido ? null : chave)}
                  className={`min-h-24 rounded-lg p-1.5 border transition-all ${
                    isHoje
                      ? 'border-blue-600 bg-blue-950/30'
                      : instsNoDia.length > 0
                      ? 'border-[var(--surface-4)] bg-[var(--surface-3)] cursor-pointer hover:border-slate-500'
                      : 'border-transparent bg-[var(--surface-2)]'
                  }`}
                >
                  {/* Número do dia */}
                  <div className={`text-xs font-medium mb-1 w-5 h-5 flex items-center justify-center rounded-full ${
                    isHoje ? 'bg-blue-600 text-white' : 'text-slate-500'
                  }`}>
                    {dia}
                  </div>

                  {/* Badges de instrumentos */}
                  <div className="space-y-0.5">
                    {instsNoDia.slice(0, expandido ? 99 : 2).map((inst) => {
                      const cfg = classeVencimento(inst.nextCalibrationDate);
                      return (
                        <div
                          key={inst._id}
                          onClick={(e) => { e.stopPropagation(); irParaInstrumento(inst); }}
                          className={`text-[10px] px-1.5 py-0.5 rounded border truncate cursor-pointer hover:opacity-80 transition-opacity ${cfg?.cor}`}
                          title={`${inst.tag} — ${inst.description}`}
                        >
                          {inst.tag}
                        </div>
                      );
                    })}
                    {!expandido && instsNoDia.length > 2 && (
                      <div className="text-[10px] text-slate-500 pl-1">
                        +{instsNoDia.length - 2} mais
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Painel lateral do dia expandido */}
          {diaExpandido && porDia[diaExpandido] && (
            <div className="mt-4 bg-[var(--surface-3)] border border-[var(--surface-4)] rounded-xl p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-slate-200">
                  Vencimentos em {diaExpandido.split('-')[2]}/{MESES[parseInt(diaExpandido.split('-')[1])]}
                </h3>
                <button onClick={() => setDiaExpandido(null)} className="text-slate-500 hover:text-slate-300 text-lg">×</button>
              </div>
              <div className="space-y-2">
                {porDia[diaExpandido].map((inst) => {
                  const cfg = classeVencimento(inst.nextCalibrationDate);
                  return (
                    <button
                      key={inst._id}
                      onClick={() => irParaInstrumento(inst)}
                      className="w-full text-left flex items-center gap-3 p-3 bg-[var(--surface-1)] rounded-lg hover:bg-[var(--surface-4)] transition-colors group"
                    >
                      <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${cfg?.dot}`} />
                      <div className="min-w-0">
                        <div className="text-sm font-mono font-bold text-slate-200">{inst.tag}</div>
                        <div className="text-xs text-slate-500 truncate">{inst.description}</div>
                      </div>
                      <span className={`ml-auto text-xs font-medium px-2 py-0.5 rounded-full border ${cfg?.cor}`}>
                        {cfg?.label}
                      </span>
                      <span className="text-slate-600 group-hover:text-slate-400 text-xs">→</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </>
      )}

      {/* ── Visão Trimestral ─────────────────────────────────────────────────── */}
      {visao === 'trimestral' && (
        <>
          <div className="flex items-center justify-between mb-4">
            <button onClick={mesAnterior} className="p-2 text-slate-400 hover:text-slate-200 hover:bg-[var(--surface-3)] rounded-lg transition-colors">
              ← Mês anterior
            </button>
            <h2 className="text-base font-semibold text-slate-200">
              {MESES[mes]} — {MESES[(mes + 2) % 12]} {ano}
            </h2>
            <button onClick={proximoMes} className="p-2 text-slate-400 hover:text-slate-200 hover:bg-[var(--surface-3)] rounded-lg transition-colors">
              Próximo mês →
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {trimestral.map(({ mes: m, ano: a, instrumentos: insts }) => (
              <div key={`${a}-${m}`} className="bg-[var(--surface-3)] border border-[var(--surface-4)] rounded-xl overflow-hidden">
                {/* Cabeçalho do mês */}
                <div className="px-4 py-3 border-b border-[var(--surface-4)] flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-slate-200">{MESES[m]} {a}</h3>
                  <span className="text-xs text-slate-500">{insts.length} vencimento{insts.length !== 1 ? 's' : ''}</span>
                </div>

                {/* Lista de instrumentos */}
                {insts.length === 0 ? (
                  <div className="px-4 py-8 text-center text-slate-600 text-sm">
                    Nenhum vencimento
                  </div>
                ) : (
                  <div className="divide-y divide-[var(--surface-4)]/50">
                    {insts.map((inst) => {
                      const cfg = classeVencimento(inst.nextCalibrationDate);
                      const dia = new Date(inst.nextCalibrationDate).getDate();
                      return (
                        <button
                          key={inst._id}
                          onClick={() => irParaInstrumento(inst)}
                          className="w-full text-left flex items-center gap-3 px-4 py-3 hover:bg-[var(--surface-4)]/50 transition-colors group"
                        >
                          <div className="text-xs text-slate-600 w-6 text-center font-mono">{dia}</div>
                          <span className={`w-2 h-2 rounded-full flex-shrink-0 ${cfg?.dot}`} />
                          <div className="min-w-0 flex-1">
                            <div className="text-xs font-mono font-bold text-slate-200">{inst.tag}</div>
                            <div className="text-[10px] text-slate-500 truncate">{inst.description}</div>
                          </div>
                          <span className={`text-[10px] px-1.5 py-0.5 rounded border ${cfg?.cor}`}>
                            {cfg?.label}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}